import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import "./PopupStyles.css";
import CloseIcon from "@mui/icons-material/Close";
import TextInput from "../TextInput/TextInput";
import copyIcon from "../../Assets/Images/copy-icon.png";
import { Toast } from "../../Common/Toast-Snackbar/ToastSnackbar";
import linkedINIcon from "../../Assets/Images/linkedinIcon.png";
import whatsAppIcon from "../../Assets/Images/whatsapp-icon.png";
import AnimationPlayer from "../Animation-Player/AnimationPlayer";

const renderPopupPortal = (content) => {
  if (typeof document === "undefined") {
    return content;
  }

  return createPortal(content, document.body);
};

export const StandardPopup = ({
  isOpen,
  onClose,
  title,
  subTitle = "",
  children,
  submitLabel = "Submit",
  cancelLabel = "Cancel",
  onSubmit,
  hideActions = false,
  width = "auto",
  maxHeight = "90vh",
  isSubmitting = false,
  popupStyle = {},
  headerStyle = {},
  headerRightComponent,
  contentStyle,
}) => {
  const [isVisible, setIsVisible] = useState(isOpen);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      document.body.classList.add("no-scroll");
      setTimeout(() => setIsAnimating(true), 10);
    } else {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setIsVisible(false);
        document.body.classList.remove("no-scroll");
      }, 200);
      return () => clearTimeout(timer);
    }
    return () => document.body.classList.remove("no-scroll");
  }, [isOpen]);

  if (!isVisible) return null;

  return renderPopupPortal(
    <div className="popup-overlay" onClick={() => onClose()}>
      <div
        className="popup-container"
        onClick={(e) => e.stopPropagation()}
        style={{
          width,
          maxHeight,
          transform: isAnimating ? "scale(1)" : "scale(0.9)",
          opacity: isAnimating ? 1 : 0,
          ...popupStyle,
        }}
      >
        <div className="popup-header" style={headerStyle}>
          <div className="popup-title-container">
            <h2 className="popup-title">{title}</h2>
            {subTitle && <div className="popup-subtitle">{subTitle}</div>}
          </div>
          {headerRightComponent && (
            <div style={{ marginLeft: "auto", marginRight: "10px" }}>
              {headerRightComponent}
            </div>
          )}
          <button className="popup-close" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        <div className="popup-content" style={contentStyle}>
          {children}
        </div>

        {!hideActions && (
          <div className="popup-actions">
            <button
              className="popup-button popup-button-cancel"
              onClick={onClose}
              type="button"
            >
              {cancelLabel}
            </button>
            <button
              className="popup-button popup-button-primary"
              onClick={onSubmit}
              disabled={isSubmitting}
              type="button"
            >
              {isSubmitting ? "Submitting..." : submitLabel}
            </button>
          </div>
        )}
      </div>
    </div>,
  );
};
export const SidePopup = ({
  isOpen,
  onClose,
  title,
  subTitle = "",
  children,
  width = "40%",
  headerRightComponent,
}) => {
  const [isVisible, setIsVisible] = useState(isOpen);
  const [isAnimating, setIsAnimating] = useState(false);
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      document.body.classList.add("no-scroll");
      setTimeout(() => setIsAnimating(true), 10);
    } else {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setIsVisible(false);
        document.body.classList.remove("no-scroll");
      }, 200);
      return () => clearTimeout(timer);
    }
    return () => document.body.classList.remove("no-scroll");
  }, [isOpen]);

  if (!isVisible) return null;

  return renderPopupPortal(
    <>
      <div className="popup-overlay" onClick={onClose}></div>
      <div
        className="side-popup-container"
        style={{
          width,
          transform: isAnimating ? "translateX(0)" : "translateX(100%)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sidepopup-header">
          <div className="popup-title-container">
            <h2 className="popup-title">{title}</h2>
            {subTitle && <div className="popup-subtitle">{subTitle}</div>}
          </div>
          {headerRightComponent && (
            <div style={{ marginLeft: "auto", marginRight: "10px" }}>
              {headerRightComponent}
            </div>
          )}
          <button className="popup-close" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        <div className="popup-content">{children}</div>
      </div>
    </>,
  );
};
export const ConfirmationPopup = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  subTitle = "",
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  isSubmitting = false,
  showCloseIcon = false,
  animationData = null,
  animationHeight = 150,
  animationWidth = 150,
  confirmButtonClass = "popup-button-primary",
}) => {
  const [isVisible, setIsVisible] = useState(isOpen);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      document.body.classList.add("no-scroll");
      setTimeout(() => setIsAnimating(true), 10);
    } else {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setIsVisible(false);
        document.body.classList.remove("no-scroll");
      }, 200);
      return () => clearTimeout(timer);
    }
    return () => document.body.classList.remove("no-scroll");
  }, [isOpen]);

  if (!isVisible) return null;

  return renderPopupPortal(
    <div className="popup-overlay" onClick={onClose}>
      <div
        className="popup-container confirmation-popup"
        onClick={(e) => e.stopPropagation()}
        style={{
          transform: isAnimating ? "scale(1)" : "scale(0.9)",
          opacity: isAnimating ? 1 : 0,
        }}
      >
        <div className="popup-header">
          <div className="popup-title-container">
            <h2 className="popup-title">{title}</h2>
            {subTitle && <div className="popup-subtitle">{subTitle}</div>}
          </div>
          {showCloseIcon && (
            <button className="popup-close" onClick={onClose}>
              <CloseIcon />
            </button>
          )}
        </div>

        <div className="popup-content">
          <p className="confirmation-message">{message}</p>
          {animationData && (
            <div
              className="confirmation-animation"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AnimationPlayer
                animationSrc={animationData}
                loop={true}
                autoplay={true}
                playerHeight={animationHeight}
                playerWidth={animationWidth}
              />
            </div>
          )}
        </div>

        <div className="popup-actions">
          {cancelLabel && (
            <button
              className="popup-button popup-button-cancel"
              onClick={onClose}
              type="button"
            >
              {cancelLabel}
            </button>
          )}

          <button
            className={`popup-button popup-button-primary ${confirmButtonClass}`}
            onClick={onConfirm}
            disabled={isSubmitting}
            type="button"
          >
            {isSubmitting ? "Processing..." : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
  );
};
export const SessionPopup = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  subTitle = "",
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  isSubmitting = false,
  showCloseIcon = false,
  animationData = null,
  animationHeight = 150,
  animationWidth = 150,
  headerIcon,
}) => {
  const [isVisible, setIsVisible] = useState(isOpen);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      document.body.classList.add("no-scroll");
      setTimeout(() => setIsAnimating(true), 10);
    } else {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setIsVisible(false);
        document.body.classList.remove("no-scroll");
      }, 200);
      return () => clearTimeout(timer);
    }
    return () => document.body.classList.remove("no-scroll");
  }, [isOpen]);

  if (!isVisible) return null;

  return renderPopupPortal(
    <div className="popup-overlay" onClick={onClose}>
      <div
        className="popup-container confirmation-popup"
        onClick={(e) => e.stopPropagation()}
        style={{
          transform: isAnimating ? "scale(1)" : "scale(0.9)",
          opacity: isAnimating ? 1 : 0,
          overflow: "visible",
        }}
      >
        {headerIcon && (
          <div
            style={{
              position: "absolute",
              top: "-60px",
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 10,
            }}
          >
            <img src={headerIcon} alt="icon" style={{ height: "110px" }} />
          </div>
        )}
        <div
          className="session-popup-header"
          style={{ paddingTop: headerIcon ? "45px" : "0" }}
        >
          <div
            className="popup-title-container"
            style={{ flex: 1, textAlign: "center" }}
          >
            <h2 className="popup-title">{title}</h2>
            {subTitle && <div className="popup-subtitle">{subTitle}</div>}
          </div>
          {showCloseIcon && (
            <button className="popup-close" onClick={onClose}>
              <CloseIcon />
            </button>
          )}
        </div>

        <div className="session-popup-content">
          <p className="confirmation-message">{message}</p>
          {animationData && (
            <div
              className="confirmation-animation"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AnimationPlayer
                animationSrc={animationData}
                loop={true}
                autoplay={true}
                playerHeight={animationHeight}
                playerWidth={animationWidth}
              />
            </div>
          )}
        </div>

        <div className="popup-actions">
          {cancelLabel && (
            <button
              className="popup-button popup-button-cancel"
              onClick={onClose}
              type="button"
            >
              {cancelLabel}
            </button>
          )}

          <button
            className="popup-button popup-button-primary"
            onClick={onConfirm}
            disabled={isSubmitting}
            type="button"
          >
            {isSubmitting ? "Processing..." : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
  );
};
export const MessagePopup = ({
  isOpen,
  onClose,
  onSubmit,
  title,
  subTitle = "",
  labelText = "Label",
  messagePlaceholder = "Type your message here...",
  labelPlaceholder = "Label",
  submitLabel = "Send Message",
  cancelLabel = "Cancel",
  isSubmitting = false,
  initialValues = { label: "", message: "" },
}) => {
  const [values, setValues] = React.useState(initialValues);
  const [isVisible, setIsVisible] = React.useState(isOpen);
  const [isAnimating, setIsAnimating] = React.useState(false);

  React.useEffect(() => {
    if (isOpen) {
      setValues(initialValues);
      setIsVisible(true);
      document.body.classList.add("no-scroll");
      setTimeout(() => setIsAnimating(true), 10);
    } else {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setIsVisible(false);
        document.body.classList.remove("no-scroll");
      }, 200);
      return () => clearTimeout(timer);
    }
    return () => document.body.classList.remove("no-scroll");
  }, [isOpen, initialValues]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = () => {
    if (onSubmit) {
      onSubmit(values);
    }
  };

  if (!isVisible) return null;

  return renderPopupPortal(
    <div className="popup-overlay" onClick={onClose}>
      <div
        className="popup-container"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "500px",
          transform: isAnimating ? "scale(1)" : "scale(0.9)",
          opacity: isAnimating ? 1 : 0,
        }}
      >
        <div className="popup-header">
          <div className="popup-title-container">
            <h2 className="popup-title">{title}</h2>
            {subTitle && <div className="popup-subtitle">{subTitle}</div>}
          </div>
          <button className="popup-close" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        <div className="popup-content">
          <div className="popup-field">
            <label className="popup-label">{labelText}</label>
            <input
              type="text"
              name="label"
              value={values.label}
              onChange={handleChange}
              placeholder={labelPlaceholder}
              className="popup-input"
            />
          </div>

          <div className="popup-field">
            <label className="popup-label">Message</label>
            <textarea
              name="message"
              value={values.message}
              onChange={handleChange}
              placeholder={messagePlaceholder}
              className="popup-input popup-textarea"
              rows={6}
            />
          </div>
        </div>

        <div className="popup-actions">
          <button
            className="popup-button popup-button-cancel"
            onClick={onClose}
            type="button"
          >
            {cancelLabel}
          </button>
          <button
            className="popup-button popup-button-primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
            type="button"
          >
            {isSubmitting ? "Sending..." : submitLabel}
          </button>
        </div>
      </div>
    </div>,
  );
};

export const CopyLinkPopup = React.memo(
  ({
    isOpen,
    onClose,
    title,
    link,
    jobData,
    showLinkedInIcon = true,
    showWhatsappIcon = true,
  }) => {
    const [isVisible, setIsVisible] = React.useState(isOpen);
    const [isAnimating, setIsAnimating] = React.useState(false);
    const getInitialShareContent = React.useCallback(() => {
      const jobTitle = jobData?.title;
      const location = jobData?.location;
      const salary =
        jobData?.salary_min && jobData?.salary_max
          ? `${jobData.salary_min} - ${jobData.salary_max} per annum`
          : null;

      const messageParts = [];
      if (jobTitle) messageParts.push(`\u{1F4CC} *${jobTitle}*`);
      if (location) messageParts.push(`\u{1F4CD} ${location}`);
      if (salary) messageParts.push(`\u{1F4B0} ${salary}`);

      return messageParts.length > 0
        ? `New Job Opportunity!\n\n${messageParts.join("\n")}`
        : "New Job Opportunity!";
    }, [jobData]);

    const getShareMessageContent = React.useCallback(() => {
      const trimmedMessage = getInitialShareContent().trim();

      if (!link) return trimmedMessage;

      return trimmedMessage
        ? `${trimmedMessage}\n\nApply now: ${link}`
        : `New Job Opportunity!\n\nCheck it out: ${link}`;
    }, [getInitialShareContent, link]);

    useEffect(() => {
      if (isOpen) {
        setIsVisible(true);
        document.body.classList.add("no-scroll");
        setTimeout(() => setIsAnimating(true), 10);
      } else {
        setIsAnimating(false);
        const timer = setTimeout(() => {
          setIsVisible(false);
          document.body.classList.remove("no-scroll");
        }, 200);
        return () => clearTimeout(timer);
      }
      return () => document.body.classList.remove("no-scroll");
    }, [isOpen]);

    const copyToClipboard = () => {
      navigator.clipboard.writeText(link);
      Toast("success", "Link Copied Successfully..!!");
    };
    const copyShareContentToClipboard = () => {
      navigator.clipboard.writeText(getShareMessageContent());
      Toast("success", "Content and Link Copied Successfully..!!");
    };

    const shareUrlToLinkedIN = () => {
      const LinkedINSharelink = `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(
        link,
      )}`;
      window.open(LinkedINSharelink, "_blank", "noopener,noreferrer");
    };
    const shareUrlToWhatsApp = () => {
      const whatsappMessage = getShareMessageContent();

      const whatsappShareLink = `https://api.whatsapp.com/send?text=${encodeURIComponent(
        whatsappMessage,
      )}`;

      if (link) {
        window.open(whatsappShareLink, "_blank", "noopener,noreferrer");
      } else {
        Toast("error", "Error while sharing this job.");
      }
    };
    if (!isVisible) return null;

    return renderPopupPortal(
      <div className="copy-link-popup-overlay" onClick={onClose}>
        <div
          className="copy-link-popup-container"
          onClick={(e) => e.stopPropagation()}
          style={{
            transform: isAnimating
              ? "translate(-50%, -50%) scale(1)"
              : "translate(-50%, -50%) scale(0.9)",
            opacity: isAnimating ? 1 : 0,
          }}
        >
          <div className="copy-popup-header">
            <div className="popup-title-container">
              <h2 className="popup-title">{title}</h2>
            </div>
            <button className="popup-close" onClick={onClose}>
              <CloseIcon />
            </button>
          </div>

          <div className="copy-popup-content">
            <div className="copy-field">
              <TextInput
                readOnly={true}
                value={link}
                containerStyle={{ width: "100%" }}
              />
              <div className="copy-icons-container">
                <img
                  src={copyIcon}
                  alt="copyIcon"
                  onClick={copyToClipboard}
                  className="copy-icon"
                />
                {showLinkedInIcon && (
                  <img
                    src={linkedINIcon}
                    alt="linkedINIcon"
                    className="share-icons"
                    onClick={shareUrlToLinkedIN}
                  />
                )}
                {showWhatsappIcon && (
                  <img
                    src={whatsAppIcon}
                    alt="whatsAppIcon"
                    className="share-icons"
                    onClick={shareUrlToWhatsApp}
                  />
                )}
              </div>
            </div>
            <div className="share-job-message-field">
              <textarea
                key={getShareMessageContent()}
                className="share-job-message-input"
                defaultValue={getShareMessageContent()}
                rows={8}
              />
              <img
                src={copyIcon}
                alt="copyIcon"
                onClick={copyShareContentToClipboard}
                className="copy-icon share-message-copy-icon"
              />
            </div>
          </div>
        </div>
      </div>,
    );
  },
);

export const FiltersPopup = ({
  isOpen,
  onClose,
  title,
  subTitle = "",
  children,
  submitLabel = "Submit",
  cancelLabel = "Cancel",
  onSubmit,
  hideActions = false,
  width = "auto",
  maxHeight = "90vh",
  isSubmitting = false,
}) => {
  const [isVisible, setIsVisible] = useState(isOpen);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      document.body.classList.add("no-scroll");
      // Trigger animation after a tiny delay
      setTimeout(() => setIsAnimating(true), 10);
    } else {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setIsVisible(false);
        document.body.classList.remove("no-scroll");
      }, 200);
      return () => clearTimeout(timer);
    }
    return () => document.body.classList.remove("no-scroll");
  }, [isOpen]);

  if (!isVisible) return null;

  return renderPopupPortal(
    <div className="popup-overlay" onClick={() => onClose()}>
      <div
        className="popup-container"
        onClick={(e) => e.stopPropagation()}
        style={{
          width,
          maxHeight,
          transform: isAnimating ? "scale(1)" : "scale(0.9)",
          opacity: isAnimating ? 1 : 0,
        }}
      >
        <div className="filter-popup-header">
          <div className="popup-title-container">
            <h2 className="popup-title">{title}</h2>
            {subTitle && <div className="popup-subtitle">{subTitle}</div>}
          </div>
          <button className="popup-close" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        <div className="filter-popup-content">{children}</div>

        {!hideActions && (
          <div className="popup-actions">
            <button
              className="popup-button popup-button-cancel"
              onClick={onClose}
              type="button"
            >
              {cancelLabel}
            </button>
            <button
              className="popup-button popup-button-primary"
              onClick={onSubmit}
              disabled={isSubmitting}
              type="button"
            >
              {isSubmitting ? "Submitting..." : submitLabel}
            </button>
          </div>
        )}
      </div>
    </div>,
  );
};

const PopupComponents = {
  StandardPopup,
  SidePopup,
  ConfirmationPopup,
  SessionPopup,
  MessagePopup,
  FiltersPopup,
};

export default PopupComponents;
